import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import { Toaster } from "sonner";
import { getToken } from "@/lib/api";
import { AuthProvider } from "@/lib/auth";
import CookieConsent from "@/components/CookieConsent";
import ScrollToTop from "@/components/ScrollToTop";
import GA4Tracker from "@/components/GA4Tracker";
import CopyAttribution from "@/components/CopyAttribution";
import PwaInstallBanner from "@/components/PwaInstallBanner";
import StickyRadioPlayer from "@/components/StickyRadioPlayer";
import LanguageWidget from "@/components/LanguageWidget";
import { RadioPlayerProvider } from "@/lib/radioPlayer";

// Code splitting: every non-home route is lazy-imported. This shrinks
// the initial JS bundle on `/` from ~710 KB of unused JS (per Lighthouse)
// to just the home page + chrome (Navigation/Footer). On low-end mobile
// CPUs this is the single highest-impact change for LCP/TBT.
//
// Trade-off: each first navigation into a deferred route triggers a
// chunk fetch (~30-80 KB gzipped), shown by the Suspense fallback. Once
// loaded, the chunk is browser-cached by hash filename so subsequent
// visits are instant.
//
// Lazy-load priority order:
//   - High-traffic SPA routes (episodio, colunistas, autor, busca) split.
//   - Admin routes (huge — AdminDashboardPage alone is 3.3k lines) split.
//   - Static/policy pages split (privacy, brand, guia).
//   - HomePage stays eager-loaded — it's the LCP target.
const PodcastPage         = lazy(() => import("@/pages/PodcastPage"));
// Iter250 — /podcasts e /videos foram fundidos em /podcasts-de-video.
// As páginas antigas viram apenas atalhos <Navigate>, portanto seus
// componentes React foram removidos deste bundle.
const PodcastsDeVideoPage = lazy(() => import("@/pages/PodcastsDeVideoPage"));
const TagPage             = lazy(() => import("@/pages/TagPage"));
const EpisodePage         = lazy(() => import("@/pages/EpisodePage"));
const AuthorPage          = lazy(() => import("@/pages/AuthorPage"));
const AuthorsIndexPage    = lazy(() => import("@/pages/AuthorsIndexPage"));
const ScientificRankingPage = lazy(() => import("@/pages/ScientificRankingPage"));
const CoursesPage         = lazy(() => import("@/pages/CoursesPage"));
const CourseDetailPage    = lazy(() => import("@/pages/CourseDetailPage"));
const SearchPage          = lazy(() => import("@/pages/SearchPage"));
const AdminLoginPage      = lazy(() => import("@/pages/AdminLoginPage"));
const AdminDashboardPage  = lazy(() => import("@/pages/AdminDashboardPage"));
const AdminCrmPage        = lazy(() => import("@/pages/AdminCrmPage"));
// Iter218 — Staff mgmt + change-password flow.
const AdminStaffPage      = lazy(() => import("@/pages/AdminStaffPage"));
// Iter306.2 — Templates WhatsApp do CRM.
const AdminWhatsAppTemplatesPage = lazy(() => import("@/pages/AdminWhatsAppTemplatesPage"));
const AdminScholarSubmissionPage = lazy(() => import("@/pages/AdminScholarSubmissionPage"));
const AdminUrlsBufferPage = lazy(() => import("@/pages/AdminUrlsBufferPage"));
const ChangePasswordPage  = lazy(() => import("@/pages/ChangePasswordPage"));
// Iter307.3 — Troca de senha para usuário regular após admin gerar senha provisória.
const UserChangePasswordPage = lazy(() => import("@/pages/UserChangePasswordPage"));
const AdminNewsletterPage = lazy(() => import("@/pages/AdminNewsletterPage"));
const AdminSubscribersPage = lazy(() => import("@/pages/AdminSubscribersPage"));
const AdminWorkAuthorizationsPage = lazy(() => import("@/pages/AdminWorkAuthorizationsPage"));
const AdminOutreachListsPage = lazy(() => import("@/pages/AdminOutreachListsPage"));
// Iter279 — Descoberta automática de ORCIDs para autores.
const AdminOrcidDiscoveryPage = lazy(() => import("@/pages/AdminOrcidDiscoveryPage"));
const AdminParceirosPage  = lazy(() => import("@/pages/AdminParceirosPage"));
const ColumnistsPage      = lazy(() => import("@/pages/ColumnistsPage"));
const ColumnistArticlePage = lazy(() => import("@/pages/ColumnistArticlePage"));
const JournalArchiveIndexPage = lazy(() => import("@/pages/JournalArchiveIndexPage"));
const JournalArchiveListPage  = lazy(() => import("@/pages/JournalArchiveListPage"));
const JournalArchiveArticlePage = lazy(() => import("@/pages/JournalArchiveArticlePage"));
const AdminJournalArchivePage = lazy(() => import("@/pages/AdminJournalArchivePage"));
const UserAuthPage        = lazy(() => import("@/pages/UserAuthPage"));
const MyCollectionsPage   = lazy(() => import("@/pages/MyCollectionsPage"));
const CollectionEditorPage = lazy(() => import("@/pages/CollectionEditorPage"));
const SharedCollectionPage = lazy(() => import("@/pages/SharedCollectionPage"));
const AboutCollectionsPage = lazy(() => import("@/pages/AboutCollectionsPage"));
const PrivacyPolicyPage   = lazy(() => import("@/pages/PrivacyPolicyPage"));
const PwaInstallGuidePage = lazy(() => import("@/pages/PwaInstallGuidePage"));
const BastidoresTecnicosPage = lazy(() => import("@/pages/BastidoresTecnicosPage"));
const AppSharePage        = lazy(() => import("@/pages/AppSharePage"));
const MyDataPage          = lazy(() => import("@/pages/MyDataPage"));
const MeuPortfolioPage    = lazy(() => import("@/pages/MeuPortfolioPage"));
const GuidePage           = lazy(() => import("@/pages/GuidePage"));
const PublicCollectionsPage = lazy(() => import("@/pages/PublicCollectionsPage"));
const BrandAssetsPage     = lazy(() => import("@/pages/BrandAssetsPage"));
const ShortsPage          = lazy(() => import("@/pages/ShortsPage"));
const ShortPage           = lazy(() => import("@/pages/ShortPage"));
// VideosPage extinta (Iter250) — /videos redireciona para /podcasts-de-video.
const LivrariaPage        = lazy(() => import("@/pages/LivrariaPage"));
const CreatorPublicPage   = lazy(() => import("@/pages/CreatorPublicPage"));
const CommunityPage          = lazy(() => import("@/pages/CommunityPage"));
const AboutPage           = lazy(() => import("@/pages/AboutPage"));
const TeacherGuidePage    = lazy(() => import("@/pages/TeacherGuidePage"));
const ComoCitarPage       = lazy(() => import("@/pages/ComoCitarPage"));
const TeacherGuideSharePage = lazy(() => import("@/pages/TeacherGuideSharePage"));
const ParceriasPage       = lazy(() => import("@/pages/ParceriasPage"));
const ParceriasSharePage  = lazy(() => import("@/pages/ParceriasSharePage"));
const BannerKitPage       = lazy(() => import("@/pages/BannerKitPage"));
const RadioHomePage       = lazy(() => import("@/pages/RadioHomePage"));
const RadioChannelPage    = lazy(() => import("@/pages/RadioChannelPage"));
const RadioEpisodePage    = lazy(() => import("@/pages/RadioEpisodePage"));
const BookmarkletGuidePage = lazy(() => import("@/pages/BookmarkletGuidePage"));
const FeedsPage           = lazy(() => import("@/pages/FeedsPage"));
const FeedsSharePage      = lazy(() => import("@/pages/FeedsSharePage"));
const TopicsPage          = lazy(() => import("@/pages/TopicsPage"));
const ResetPasswordPage   = lazy(() => import("@/pages/ResetPasswordPage"));
const AuthCallback        = lazy(() => import("@/pages/AuthCallback"));
const BlogIndexPage       = lazy(() => import("@/pages/BlogIndexPage"));
const BlogPostPage        = lazy(() => import("@/pages/BlogPostPage"));
const LegalNewsIndexPage  = lazy(() => import("@/pages/LegalNewsIndexPage"));
const LegalNewsArticlePage = lazy(() => import("@/pages/LegalNewsArticlePage"));
const LegalActsIndexPage  = lazy(() => import("@/pages/LegalActsIndexPage"));
const LegalActsArticlePage = lazy(() => import("@/pages/LegalActsArticlePage"));
const LegalActLBPage      = lazy(() => import("@/pages/LegalActLBPage"));
const LawShortRedirect    = lazy(() => import("@/pages/LawShortRedirect"));
const DialoguesGraphPage  = lazy(() => import("@/pages/DialoguesGraphPage"));
const DialoguesSharePage  = lazy(() => import("@/pages/DialoguesSharePage"));
const RadarHubPage        = lazy(() => import("@/pages/RadarHubPage"));
const DevSaveCadernoPreview = lazy(() => import("@/pages/DevSaveCadernoPreview"));
const MinhaBibliotecaPage = lazy(() => import("@/pages/MinhaBibliotecaPage"));
const CadernoPage         = lazy(() => import("@/pages/CadernoPage"));
const PublicCadernoPage   = lazy(() => import("@/pages/PublicCadernoPage"));
const NotificationsPage   = lazy(() => import("@/pages/NotificationsPage"));
const RadarConstitucionalPage = lazy(() => import("@/pages/RadarConstitucionalPage"));
const InformativosPage    = lazy(() => import("@/pages/InformativosPage"));
const InformativoPage     = lazy(() => import("@/pages/InformativoPage"));
const DecisaoPage         = lazy(() => import("@/pages/DecisaoPage"));
const JurisprudenciaTeorPage = lazy(() => import("@/pages/JurisprudenciaTeorPage"));
const JurisprudenciaPage = lazy(() => import("@/pages/JurisprudenciaPage"));
const ConstitucionalSharePage = lazy(() => import("@/pages/ConstitucionalSharePage"));
const NewsRadarSharePage  = lazy(() => import("@/pages/NewsRadarSharePage"));
const ActsRadarSharePage  = lazy(() => import("@/pages/ActsRadarSharePage"));
const RadarHubSharePage   = lazy(() => import("@/pages/RadarHubSharePage"));
const ComunidadeSharePage = lazy(() => import("@/pages/ComunidadeSharePage"));
const AutoresSharePage    = lazy(() => import("@/pages/AutoresSharePage"));
const PodcastsSharePage   = lazy(() => import("@/pages/PodcastsSharePage"));
const LivrariaSharePage   = lazy(() => import("@/pages/LivrariaSharePage"));
const BlogSharePage       = lazy(() => import("@/pages/BlogSharePage"));
const ArtigosSharePage    = lazy(() => import("@/pages/ArtigosSharePage"));
const TemasSharePage      = lazy(() => import("@/pages/TemasSharePage"));
const CursosSharePage     = lazy(() => import("@/pages/CursosSharePage"));
const VideosSharePage     = lazy(() => import("@/pages/VideosSharePage"));
const FototecaSharePage   = lazy(() => import("@/pages/FototecaSharePage"));
const SobreSharePage      = lazy(() => import("@/pages/SobreSharePage"));
const FototecaPage        = lazy(() => import("@/pages/FototecaPage"));
const TvPage              = lazy(() => import("@/pages/TvPage"));
const AdminRadioPage      = lazy(() => import("@/pages/AdminRadioPage"));
const RadarConcursosPage  = lazy(() => import("@/pages/RadarConcursosPage"));
const GonePage            = lazy(() => import("@/pages/GonePage"));
const VerifyEmailRedirectPage = lazy(() => import("@/pages/VerifyEmailRedirectPage"));
const NewsletterPage      = lazy(() => import("@/pages/NewsletterPage"));
const NewsletterEditionPage = lazy(() => import("@/pages/NewsletterEditionPage"));
const NewsletterArchivePage = lazy(() => import("@/pages/NewsletterArchivePage"));
const AdminBlogNewPage    = lazy(() => import("@/pages/AdminBlogNewPage"));
const AdminSeoHealthPage  = lazy(() => import("@/pages/AdminSeoHealthPage"));
const AdminNewsSourcesHealthPage = lazy(() => import("@/pages/AdminNewsSourcesHealthPage"));
const AdminSeoEditorPage  = lazy(() => import("@/pages/AdminSeoEditorPage"));
const AdminTagsManagerPage = lazy(() => import("@/pages/AdminTagsManagerPage"));
const AdminCitationAuditPage = lazy(() => import("@/pages/AdminCitationAuditPage"));
const AdminPendingEpisodesPage = lazy(() => import("@/pages/AdminPendingEpisodesPage"));
const AuthorSharePage = lazy(() => import("@/pages/AuthorSharePage"));
const AccessibilityPage = lazy(() => import("@/pages/AccessibilityPage"));
import QueryParamsSEOGate from "@/components/QueryParamsSEOGate";
import RadioRedirectGuard from "@/components/RadioRedirectGuard";
import MustChangePasswordGuard from "@/components/MustChangePasswordGuard";
import TestSlugGuard, { isToxicPath } from "@/components/TestSlugGuard";
import { useBuildVersionCheck } from "@/lib/useBuildVersionCheck";

function RequireAdmin({ children }) {
  const token = getToken();
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
}

// Iter306.2 — Gate estrito: só Admin Geral (role='admin' OU token legacy).
// Auxiliares que tentarem acessar via URL direta são redirecionados
// para /admin/crm (equivalente à UX do backend que devolve 403).
function RequireAdminGeneral({ children }) {
  const token = getToken();
  if (!token) return <Navigate to="/admin/login" replace />;
  let role = null;
  try { role = localStorage.getItem("jt_admin_role"); } catch (_) { /* ignore */ }
  // Token legacy (senha global) não grava jt_admin_role no login. Assume
  // admin quando role é null/vazio + tem token (comportamento pre-Iter218).
  if (role && role !== "admin") return <Navigate to="/admin/crm" replace />;
  return children;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Synchronous hash detection — MUST run during render (not useEffect) to win
// the race against AuthProvider's initial /users/me call.
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Iter270 — Corta cedo para paths tóxicos (Users/, _pytest_, test-celebrate)
  // ANTES do <Routes>, porque essas URLs podem casar com rotas legítimas
  // (`/episodio/:pod/:slug` casa com `_pytest_pod_...`) e disparariam
  // chamadas de API 404 desnecessárias. GonePage já emite noindex/nofollow.
  if (isToxicPath(location.pathname)) {
    return <TestSlugGuard>{null}</TestSlugGuard>;
  }
  return (
    <>
      <QueryParamsSEOGate />
      <RadioRedirectGuard />
      <MustChangePasswordGuard />
      <Routes>
        <Route path="/" element={<HomePage />} />
      <Route path="/podcast/:slug" element={<PodcastPage />} />
      {/* Iter250 — Fusão de mídia. /podcasts-de-video é a nova central;
          /podcasts e /videos permanecem como aliases client-side (302
          temporário nos 30 primeiros dias — a URL antiga responde 200 e
          o React navega imediatamente para a nova, preservando rollback
          instantâneo caso o produto queira reverter). O sitemap dinâmico
          já anuncia apenas /podcasts-de-video, tornando os aliases
          invisíveis para o Google enquanto o histórico de tráfego migra. */}
      <Route path="/podcasts-de-video" element={<PodcastsDeVideoPage />} />
      <Route path="/podcasts" element={<Navigate to="/podcasts-de-video" replace />} />
      <Route path="/tema/:tag" element={<TagPage />} />
      <Route path="/episodio/:podcastSlug/:episodeSlug" element={<EpisodePage />} />
      <Route path="/episodio/:id" element={<EpisodePage />} />
      <Route path="/autor/:slug" element={<AuthorPage />} />
      <Route path="/autor/:slug/divulgar" element={<AuthorSharePage />} />
      <Route path="/autores" element={<AuthorsIndexPage />} />
      <Route path="/autores/ranking-cientifico" element={<ScientificRankingPage />} />
      <Route path="/cursos" element={<CoursesPage />} />
      <Route path="/cursos/:slug" element={<CourseDetailPage />} />
      <Route path="/busca" element={<SearchPage />} />
      <Route path="/videos" element={<Navigate to="/podcasts-de-video" replace />} />
      <Route path="/livraria" element={<LivrariaPage />} />
      {/* User accounts & collections */}
      <Route path="/conta" element={<UserAuthPage />} />
      <Route path="/bookmarklet" element={<BookmarkletGuidePage />} />
      <Route path="/feeds" element={<FeedsPage />} />
      <Route path="/feeds/divulgar" element={<FeedsSharePage />} />
      <Route path="/sobre-colecoes" element={<AboutCollectionsPage />} />
      {/* Historic alias — Google indexou /sobre/colecoes (com barra)
          em 2025. Agora redirecionamos para o canonical /sobre-colecoes
          (com hífen) via <Navigate replace>, eliminando a duplicação
          que aparecia como "Cópia com canonical diferente" no Search
          Console. */}
      <Route path="/sobre/colecoes" element={<Navigate to="/sobre-colecoes" replace />} />
      <Route path="/minhas-colecoes" element={<MyCollectionsPage />} />
      <Route path="/colecao/:id/editar" element={<CollectionEditorPage />} />
      <Route path="/c/:slug" element={<SharedCollectionPage />} />
      <Route path="/colecoes" element={<PublicCollectionsPage />} />
      <Route path="/meus-dados" element={<MyDataPage />} />
      <Route path="/minha-biblioteca" element={<MinhaBibliotecaPage />} />
      <Route path="/caderno/:slug" element={<CadernoPage />} />
      <Route path="/p/caderno/:token" element={<PublicCadernoPage />} />
      <Route path="/notificacoes" element={<NotificationsPage />} />
      <Route path="/meu-portfolio" element={<MeuPortfolioPage />} />
      <Route path="/guia" element={<GuidePage />} />
      <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
      <Route path="/marca" element={<BrandAssetsPage />} />
      {/* PWA install guide — destino do Smart Banner (Android fallback,
          iOS instrução manual obrigatória do Safari). */}
      <Route path="/app" element={<PwaInstallGuidePage />} />
      <Route path="/bastidores-tecnicos" element={<BastidoresTecnicosPage />} />
      <Route path="/app/divulgar" element={<AppSharePage />} />
      <Route path="/shorts" element={<ShortsPage />} />
      <Route path="/shorts/:id" element={<ShortPage />} />
      <Route path="/criador/:userId" element={<CreatorPublicPage />} />
      <Route path="/criadores" element={<Navigate to="/comunidade" replace />} />
      <Route path="/hall-da-fama" element={<Navigate to="/comunidade" replace />} />
      <Route path="/comunidade" element={<CommunityPage />} />
      <Route path="/sobre" element={<AboutPage />} />
      <Route path="/guia-professores" element={<TeacherGuidePage />} />
      <Route path="/guia-professores/divulgar" element={<TeacherGuideSharePage />} />
      <Route path="/sobre/guia-professores" element={<Navigate to="/guia-professores" replace />} />
      {/* Iter260 — Guia acadêmico de citação (ABNT/APA/Chicago/BibTeX). */}
      <Route path="/como-citar" element={<ComoCitarPage />} />
      <Route path="/parcerias" element={<ParceriasPage />} />
      <Route path="/parcerias/divulgar" element={<ParceriasSharePage />} />
      <Route path="/parcerias/kit-banners" element={<BannerKitPage />} />
      <Route path="/institucional/parcerias" element={<Navigate to="/parcerias" replace />} />
      {/* Rádio JurisTube — acervo de podcasts em áudio. Sticky player
          global mantém reprodução enquanto o usuário navega. */}
      <Route path="/radio" element={<RadioHomePage />} />
      <Route path="/radio/:canal" element={<RadioChannelPage />} />
      <Route path="/radio/:canal/:episodio" element={<RadioEpisodePage />} />
      <Route path="/temas" element={<TopicsPage />} />
      <Route path="/colunistas" element={<ColumnistsPage />} />
      <Route path="/artigos" element={<ColumnistsPage />} />
      <Route path="/colunistas/:author/:slug" element={<ColumnistArticlePage />} />
      <Route path="/acervo-revistas" element={<JournalArchiveIndexPage />} />
      <Route path="/acervo-revistas/artigo/:slug" element={<JournalArchiveArticlePage />} />
      <Route path="/acervo-revistas/:journal" element={<JournalArchiveListPage />} />
      <Route path="/blog" element={<BlogIndexPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/noticias-juridicas" element={<LegalNewsIndexPage />} />
      <Route path="/noticias-juridicas/:slug" element={<LegalNewsArticlePage />} />
      <Route path="/noticias-legislativas" element={<LegalActsIndexPage />} />
      <Route path="/noticias-legislativas/lb/:slug" element={<LegalActLBPage />} />
      {/* URL curta resiliente para divulgação ("/lei/9784") — resolve
          o slug atual via API e redireciona. Funciona mesmo se o slug
          mudar no admin. */}
      <Route path="/lei/:number" element={<LawShortRedirect />} />
      <Route path="/noticias-legislativas/:slug" element={<LegalActsArticlePage />} />
      <Route path="/dialogos-doutrinarios" element={<DialoguesGraphPage />} />
      <Route path="/dialogos-doutrinarios/divulgar" element={<DialoguesSharePage />} />
      {/* Hub Radar — sumário com os cinco radares; substitui o slot
          de "Coleções" no menu superior. */}
      <Route path="/radar" element={<RadarHubPage />} />
      <Route path="/radar-concursos" element={<RadarConcursosPage />} />
      <Route path="/radar/divulgar" element={<RadarHubSharePage />} />
      <Route path="/radar-constitucional" element={<RadarConstitucionalPage />} />
      <Route path="/informativos" element={<InformativosPage />} />
      <Route path="/informativos/:tribunal/:numero" element={<InformativoPage />} />
      <Route path="/decisao/:slug" element={<DecisaoPage />} />
      <Route path="/radar-constitucional/divulgar" element={<ConstitucionalSharePage />} />
      <Route path="/radar-constitucional/jurisprudencia/:slug" element={<JurisprudenciaTeorPage />} />
      <Route path="/jurisprudencia/:slug" element={<JurisprudenciaPage />} />
      <Route path="/radar-constitucional/:slug" element={<RadarConstitucionalPage />} />
      <Route path="/comunidade/divulgar" element={<ComunidadeSharePage />} />
      <Route path="/autores/divulgar" element={<AutoresSharePage />} />
      <Route path="/podcasts/divulgar" element={<PodcastsSharePage />} />
      <Route path="/livraria/divulgar" element={<LivrariaSharePage />} />
      <Route path="/blog/divulgar" element={<BlogSharePage />} />
      <Route path="/colunistas/divulgar" element={<ArtigosSharePage />} />
      <Route path="/artigos/divulgar" element={<ArtigosSharePage />} />
      <Route path="/temas/divulgar" element={<TemasSharePage />} />
      <Route path="/cursos/divulgar" element={<CursosSharePage />} />
      <Route path="/videos/divulgar" element={<VideosSharePage />} />
      <Route path="/fototeca/divulgar" element={<FototecaSharePage />} />
      <Route path="/sobre/divulgar" element={<SobreSharePage />} />
      <Route path="/fototeca" element={<FototecaPage />} />
      {/* TV JurisTube — theater mode com 2 canais (TV Justiça + acervo) */}
      <Route path="/tv" element={<TvPage />} />
      <Route path="/tv-juristube" element={<Navigate to="/tv" replace />} />
      {/* Aliases SEO/Marketing para cada radar individual */}
      <Route path="/radar-doutrinario" element={<Navigate to="/dialogos-doutrinarios" replace />} />
      <Route path="/radar-de-noticias" element={<Navigate to="/noticias-juridicas" replace />} />
      <Route path="/radar-normativo" element={<Navigate to="/noticias-legislativas" replace />} />
      {/* Kits de divulgação dos radares de notícias e normativo */}
      <Route path="/noticias-juridicas/divulgar" element={<NewsRadarSharePage />} />
      <Route path="/radar-de-noticias/divulgar" element={<NewsRadarSharePage />} />
      <Route path="/noticias-legislativas/divulgar" element={<ActsRadarSharePage />} />
      <Route path="/radar-normativo/divulgar" element={<ActsRadarSharePage />} />
      <Route path="/newsletter" element={<NewsletterPage />} />
      <Route path="/newsletter/edicao" element={<NewsletterEditionPage />} />
      <Route path="/newsletter/edicao/:run_id" element={<NewsletterEditionPage />} />
      <Route path="/newsletter/arquivo" element={<NewsletterArchivePage />} />
      <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
      <Route path="/acessibilidade" element={<AccessibilityPage />} />
      {/* Admin */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/dev/save-caderno" element={<DevSaveCadernoPreview />} />
      <Route path="/admin" element={<RequireAdmin><AdminDashboardPage /></RequireAdmin>} />
      <Route path="/admin/crm" element={<RequireAdmin><AdminCrmPage /></RequireAdmin>} />
      <Route path="/admin/staff" element={<RequireAdmin><AdminStaffPage /></RequireAdmin>} />
      <Route path="/admin/whatsapp-templates" element={<RequireAdminGeneral><AdminWhatsAppTemplatesPage /></RequireAdminGeneral>} />
      <Route path="/admin/scholar-submission" element={<RequireAdmin><AdminScholarSubmissionPage /></RequireAdmin>} />
      <Route path="/admin/urls-buffer" element={<RequireAdmin><AdminUrlsBufferPage /></RequireAdmin>} />
      <Route path="/admin/change-password" element={<RequireAdmin><ChangePasswordPage /></RequireAdmin>} />
      {/* Iter307.3 — Rota pública para usuário logado trocar a própria senha
          (fluxo forçado após senha provisória gerada pelo admin OU troca
          voluntária). O guard de autenticação está dentro da própria página. */}
      <Route path="/conta/mudar-senha" element={<UserChangePasswordPage />} />
      <Route path="/admin/blog/novo" element={<RequireAdmin><AdminBlogNewPage /></RequireAdmin>} />
      <Route path="/admin/blog/editar/:slug" element={<RequireAdmin><AdminBlogNewPage /></RequireAdmin>} />
      <Route path="/admin/seo-health" element={<RequireAdmin><AdminSeoHealthPage /></RequireAdmin>} />
      <Route path="/admin/fontes-noticias" element={<RequireAdmin><AdminNewsSourcesHealthPage /></RequireAdmin>} />
      <Route path="/admin/seo" element={<RequireAdmin><AdminSeoEditorPage /></RequireAdmin>} />
      <Route path="/admin/tags-manager" element={<RequireAdmin><AdminTagsManagerPage /></RequireAdmin>} />
      <Route path="/admin/citation-audit" element={<RequireAdmin><AdminCitationAuditPage /></RequireAdmin>} />
      <Route path="/admin/pending-episodes" element={<RequireAdmin><AdminPendingEpisodesPage /></RequireAdmin>} />
      <Route path="/admin/radio" element={<RequireAdmin><AdminRadioPage /></RequireAdmin>} />
      <Route path="/admin/newsletter" element={<RequireAdmin><AdminNewsletterPage /></RequireAdmin>} />
      <Route path="/admin/cadastrados" element={<RequireAdmin><AdminSubscribersPage /></RequireAdmin>} />
      <Route path="/admin/autorizacoes-obras" element={<RequireAdmin><AdminWorkAuthorizationsPage /></RequireAdmin>} />
      <Route path="/admin/parceiros" element={<RequireAdmin><AdminParceirosPage /></RequireAdmin>} />
      <Route path="/admin/outreach" element={<RequireAdmin><AdminOutreachListsPage /></RequireAdmin>} />
      {/* Iter279 — Curadoria dos candidatos ORCID descobertos. */}
      <Route path="/admin/orcid/candidates" element={<RequireAdmin><AdminOrcidDiscoveryPage /></RequireAdmin>} />
      <Route path="/admin/acervo-revistas" element={<RequireAdmin><AdminJournalArchivePage /></RequireAdmin>} />

      {/* E-mail de confirmação de cadastro aterrissa aqui — redirect
          imediato pro endpoint backend que renderiza a página temática
          com o resultado da verificação. Sem essa rota, o link do
          e-mail caía no catch-all /<GonePage>, mostrando uma falsa
          "página inexistente" e prejudicando a primeira impressão do
          recém-cadastrado. */}
      <Route path="/verificar-email" element={<VerifyEmailRedirectPage />} />

      {/* SEO hygiene — paths que apareceram no Search Console como
          "Página com redirecionamento" / "Cópia" / "Soft 404" mas que
          eram lixo (URLs de Word colado, endpoints antigos, atalhos
          internos). Cada um renderiza <GonePage/>, que emite
          <meta robots noindex,nofollow> e mensagem humana clara.
          Isso acelera a remoção do índice (em vez de redirecionar
          tudo para /, que confundia o Google). */}
      <Route path="/Users/*" element={<GonePage />} />
      <Route path="/management" element={<GonePage />} />
      <Route path="/add-video" element={<GonePage />} />
      <Route path="/all-episodes" element={<GonePage />} />
      <Route path="/login" element={<Navigate to="/conta" replace />} />
      <Route path="/colecao" element={<Navigate to="/colecoes" replace />} />
      <Route path="/episode/:n" element={<GonePage />} />
      <Route path="/podcast" element={<Navigate to="/podcasts-de-video" replace />} />

      {/* Catch-all: render GonePage (noindex) em vez de redirect→/.
          Antes, qualquer URL desconhecida era 301 para /, o que
          inflava a categoria "Página com redirecionamento" no Search
          Console sem nunca permitir que o Google removesse a entrada
          fantasma do índice. */}
      <Route path="*" element={<GonePage />} />
      </Routes>
    </>
  );
}

// Minimal Suspense fallback — no spinner that would compete with the LCP
// element, just an empty container with the same min-height as the
// chrome-less viewport so the layout doesn't jump while chunks fetch.
function RouteFallback() {
  return <div style={{ minHeight: "60vh" }} aria-hidden="true" />;
}

function App() {
  // Iter212 — Polling proativo de versão. Ativado uma vez por sessão.
  useBuildVersionCheck();
  useEffect(() => {
    // Once react-helmet-async has had a chance to mount and inject its
    // route-specific <link rel="canonical"> + <meta property="og:url">,
    // remove the bootstrap tags we seeded in index.html. Keeping both
    // would leave the page with TWO canonicals, which Google treats as
    // "no canonical declared" and groups URLs as duplicates anyway.
    // requestAnimationFrame waits one frame past hydration; that's enough
    // for Helmet to flush its sync queue.
    requestAnimationFrame(() => {
      document.querySelectorAll('[data-bootstrap="1"], [data-prerender="1"], #jt-ssr-style').forEach((n) => n.remove());
    });

    // PWA Service Worker — registra após o load para não competir com
    // a hidratação inicial. Mantemos em produção e preview; em dev local
    // (npm start sem HTTPS) o navegador ignora silenciosamente.
    if ("serviceWorker" in navigator) {
      const reg = () => {
        // Iter241 — ``updateViaCache: "none"`` faz o browser IGNORAR
        // o HTTP cache (inclusive de proxies corporativos) ao buscar
        // /sw.js durante checagens de update. Sem isso, um proxy que
        // cacheia sw.js por 24h efetivamente congela o SW numa versão
        // antiga e o site nunca detecta que precisa atualizar chunks.
        navigator.serviceWorker
          .register("/sw.js", { scope: "/", updateViaCache: "none" })
          .catch(() => { /* sw indisponível — site funciona normalmente */ });
      };
      if (document.readyState === "complete") reg();
      else window.addEventListener("load", reg, { once: true });
    }
  }, []);
  return (
    <HelmetProvider>
      <AuthProvider>
      <RadioPlayerProvider>
      <BrowserRouter>
        <ScrollToTop />
        <GA4Tracker />
        <CopyAttribution />
        <div className="min-h-screen flex flex-col grain">
          <Navigation />
          <main className="flex-1 relative z-10">
            <Suspense fallback={<RouteFallback />}>
              <AppRouter />
            </Suspense>
          </main>
          <Footer />
          <CookieConsent />
          <PwaInstallBanner />
          <StickyRadioPlayer />
          <LanguageWidget />
          <Toaster position="bottom-right" theme="dark" richColors closeButton visibleToasts={5} />
        </div>
      </BrowserRouter>
      </RadioPlayerProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
